import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { Coupon } from '@cds/betting-offer/domain-specific/coupons';
import { FixtureFormMode, SortByCriteria } from '@cds/query-objects';
import { EpcotConfig, MultiBuilderConfig } from '@frontend/sports/common/client-config-data-access';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { sortBy } from 'lodash-es';
import { Observable, firstValueFrom, map, switchMap } from 'rxjs';

import { CouponApi } from '../cds/coupon-api.service';
import { StylesLazyLoadingService } from '../deferred/styles/style-loading.service';
import { StatisticsConfigService } from '../statistics/statistics-config.service';
import { SubNavigationItem } from '../sub-navigation/sub-navigation.models';
import { MultiBuilderCoupon, MultibuilderResponse } from './multi-builder.model';
import { MultiBuilderService } from './multi-builder.service';

@Injectable()
export class MultiBuilderResolver {
    multibuilderResponse: MultibuilderResponse | undefined;

    constructor(
        private couponApi: CouponApi,
        private multiBuilderConfig: MultiBuilderConfig,
        private multiBuilderService: MultiBuilderService,
        private urlHelper: UrlHelperService,
        private redirectHelper: RedirectHelperService,
        private statisticsConfigService: StatisticsConfigService,
        private stylesLazyLoadingService: StylesLazyLoadingService,
        private epcotConfig: EpcotConfig,
    ) {}

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<MultibuilderResponse | undefined> {
        return firstValueFrom(
            this.multiBuilderService.getMultibuilderCoupons().pipe(
                switchMap(async (coupons) => {
                    this.multiBuilderService.setComboErrorToasterOpened(false);
                    let subNavigationItems: SubNavigationItem[] = [];
                    const selectedCouponId = Number(route.params.coupon);
                    if (coupons?.length) {
                        subNavigationItems = this.getOrderedCompetitions(coupons, selectedCouponId);
                    }
                    if (subNavigationItems.length) {
                        const couponFixtures = this.getCouponFixtures(subNavigationItems, selectedCouponId, this.multiBuilderConfig.maxFixturesCount);
                        let multiBuilderStyles = [this.stylesLazyLoadingService.lazyLoad('multi-builder')];
                        if (this.epcotConfig.isEnabled) {
                            multiBuilderStyles = [...multiBuilderStyles, this.stylesLazyLoadingService.lazyLoad('epcot-multi-builder')];
                        }
                        [this.multibuilderResponse] = await Promise.all([firstValueFrom(couponFixtures), ...multiBuilderStyles]);

                        return this.multibuilderResponse;
                    }
                    this.redirectHelper.goHome();

                    return this.multibuilderResponse;
                }),
            ),
        );
    }

    private getCouponFixtures(
        subNavigationItems: SubNavigationItem[],
        selectedCouponId: number,
        take: number,
    ): Observable<MultibuilderResponse | undefined> {
        const fromDate = DateProviderService.getStartOfDay(DateProviderService.getDate());
        const toDate = DateProviderService.getEndOfDay(fromDate, 6);
        const statModes = this.statisticsConfigService.getStatisticsModesBySportId(undefined);

        return this.couponApi
            .getMultiBuilderCoupon(selectedCouponId, fromDate, toDate, SortByCriteria.StartDate, take, FixtureFormMode.All, statModes)
            .pipe(map((fixtures) => ({ subNavigationItems, fixtures })));
    }

    private getOrderedCompetitions(model: Coupon[], selectedCouponId: number): SubNavigationItem[] {
        const multiBuilderCoupons = sortBy(model, (coupon) => coupon.order);

        if (!multiBuilderCoupons.length) {
            return [];
        }

        return this.getCompetitionTabs(multiBuilderCoupons, selectedCouponId);
    }

    private getCompetitionTabs(multiBuilderCoupons: MultiBuilderCoupon[], selectedCouponId: number): SubNavigationItem[] {
        const competitiontabs: SubNavigationItem[] = [];
        multiBuilderCoupons.forEach((multiBuilderCoupon) => {
            if (multiBuilderCoupon) {
                let isActive = false;

                if (selectedCouponId && multiBuilderCoupon.id === selectedCouponId) {
                    isActive = true;
                }

                competitiontabs.push({
                    id: multiBuilderCoupon.id,
                    title: multiBuilderCoupon.title,
                    active: isActive,
                    ignoreBrowserHistory: true,
                    url: this.urlHelper.getMultiBuilderUrl({
                        id: multiBuilderCoupon.id,
                        name: multiBuilderCoupon.name,
                    }),
                    iconClass: multiBuilderCoupon.iconClass,
                });
            }
        });

        return competitiontabs;
    }
}
